import React, { useCallback, useEffect, useState } from "react";
import { Drawer, Button, Select, Space, Input, message } from "antd";
import { PrinterOutlined, SendOutlined, WhatsAppOutlined, MessageOutlined } from "@ant-design/icons";
import { PageSizeType, TemplatesTypes } from "../templates";
import { useKeyboardShortcuts, useIsMobile, useIsTablet } from "../../Hook";
import {
  SHORTCUTS,
  INVOICE_TEMPLATES,
  INVOICE_PAGE_SIZES,
} from "../../Constant";
import {
  useSendOrderInvoiceWhatsAppMutation,
  useSendWhatsAppTextMessageMutation,
  useSendSMSMutation,
} from "../../Services";
import { useInvoiceData } from "./useInvoiceData";
import { useSystemConfig } from "../../Hook/useSystemConfig";
import { useInvoiceDocument } from "./useInvoiceDocument";
import { CourierParcelDrawer } from "../CourierParcelDrawer/CourierParcelDrawer";
import { DRAWER_FORM_WIDTH, drawerTitle } from "../AppUi";

interface InvoiceModalProps {
  open: boolean;
  orderId: string | null;
  onClose: () => void;
}

export const InvoiceModal: React.FC<InvoiceModalProps> = ({
  open,
  orderId,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [pageSize, setPageSize] = useState<PageSizeType>("80mm");
  const [template, setTemplate] = useState<TemplatesTypes>(
    TemplatesTypes.Classic,
  );

  const { order, config } = useInvoiceData({ open, orderId });
  const { getInvoiceHtml, printInvoice, downloadInvoice, getInvoicePdfBase64 } =
    useInvoiceDocument({
      order,
      config,
      pageSize,
      template,
    });

  const [whatsappPhone, setWhatsappPhone] = useState<string>("");
  const [courierOpen, setCourierOpen] = useState(false);
  const sendInvoiceMutation = useSendOrderInvoiceWhatsAppMutation();
  const sendWhatsAppTextMutation = useSendWhatsAppTextMessageMutation();
  const sendSmsMutation = useSendSMSMutation();
  const { isWhatsAppEnabled, isSmsGatewayEnable } = useSystemConfig();
  const isWhatsAppConnected = true;//useWhatsAppConnected();

  useEffect(() => {
    if (order) {
      setWhatsappPhone((prev) =>
        prev.trim()
          ? prev
          : String(order.customer_phone || config.whatsapp || ""),
      );
    }
  }, [order, config]);

  const handleSendWhatsApp = useCallback(async () => {
    if (!order || !whatsappPhone.trim()) {
      message.warning(
        "Enter the WhatsApp phone number to send the invoice to.",
      );
      return;
    }

    try {
      const pdfBase64 = await getInvoicePdfBase64();
      if (!pdfBase64) {
        message.error("Failed to generate the invoice PDF.");
        return;
      }

      const res = await sendInvoiceMutation.mutateAsync({
        order_id: String(order.id),
        phone: whatsappPhone.trim(),
        pdf_base64: pdfBase64,
        filename: `Invoice_${String(order.order_no || "order")}.pdf`,
      });

      if (res?.success) {
        message.success("Invoice sent via WhatsApp!");
      } else {
        message.error(res?.message || "Failed to send the invoice.");
      }
    } catch (error: any) {
      message.error(error?.message || "Failed to send the invoice.");
    }
  }, [getInvoicePdfBase64, order, whatsappPhone, sendInvoiceMutation]);

  const handleSendWhatsAppLink = useCallback(async () => {
    if (!order || !whatsappPhone.trim()) {
      message.warning("Enter the phone number to send the invoice link.");
      return;
    }
    try {
      const link = `${window.location.origin}/${localStorage.getItem("tenantId") || ""}/${order.id}`;
      const textMessage = `Dear Customer, thank you for your business. You can view/download your invoice #${order.order_no} here: ${link}`;
      
      const res = await sendWhatsAppTextMutation.mutateAsync({
        phone: whatsappPhone.trim(),
        message: textMessage,
      });

      if (res?.success) {
        message.success("Invoice link sent via WhatsApp!");
      } else {
        message.error(res?.message || "Failed to send the invoice link.");
      }
    } catch (error: any) {
      message.error(error?.message || "Failed to send the invoice link.");
    }
  }, [order, whatsappPhone, sendWhatsAppTextMutation]);

  const handleSendSMSLink = useCallback(async () => {
    if (!order || !whatsappPhone.trim()) {
      message.warning("Enter the phone number to send the invoice link.");
      return;
    }
    try {
      const link = `${window.location.origin}/${localStorage.getItem("tenantId") || ""}/${order.id}`;
      const textMessage = `Dear Customer, thank you for your business. You can view/download your invoice #${order.order_no} here: ${link}`;
      
      const res = await sendSmsMutation.mutateAsync({
        contact: whatsappPhone.trim(),
        message: textMessage,
      });

      if (res?.success) {
        message.success("Invoice link sent via SMS!");
      } else {
        message.error(res?.message || "Failed to send the invoice link.");
      }
    } catch (error: any) {
      message.error(error?.message || "Failed to send the invoice link.");
    }
  }, [order, whatsappPhone, sendSmsMutation]);

  const cycleTemplate = useCallback(() => {
    setTemplate(TemplatesTypes.Classic);
  }, []);

  const cyclePageSize = useCallback(() => {
    setPageSize((prev) => {
      const idx = INVOICE_PAGE_SIZES.findIndex((s) => s.value === prev);
      return INVOICE_PAGE_SIZES[(idx + 1) % INVOICE_PAGE_SIZES.length].value;
    });
  }, []);

  useKeyboardShortcuts([
    {
      shortcut: SHORTCUTS.INVOICE_PRINT.PRINT_INVOICE,
      options: { enabled: open, enableOnFormTags: false, enableOnContentEditable: false },
      handler: printInvoice,
    },
    {
      shortcut: SHORTCUTS.INVOICE_PRINT.DOWNLOAD_PDF,
      options: { enabled: open, enableOnFormTags: false, enableOnContentEditable: false },
      handler: downloadInvoice,
    },
    {
      shortcut: SHORTCUTS.INVOICE_PRINT.CHANGE_TEMPLATE,
      options: { enabled: open, enableOnFormTags: false, enableOnContentEditable: false },
      handler: cycleTemplate,
    },
    {
      shortcut: SHORTCUTS.INVOICE_PRINT.CHANGE_PAGE_SIZE,
      options: { enabled: open, enableOnFormTags: false, enableOnContentEditable: false },
      handler: cyclePageSize,
    },
  ]);

  if (!order) return null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={drawerTitle("Invoice Preview")}
      width={isMobile || isTablet ? "100%" : DRAWER_FORM_WIDTH}
      className="invoice-drawer"
      destroyOnClose
      footer={
        <div className="flex flex-col gap-2 w-full">
          <div className="flex flex-wrap gap-2 w-full">
            <Button
              type="primary"
              size="middle"
              onClick={printInvoice}
              id="print_invoice_pdf"
              className="flex-grow min-w-[100px] text-xs font-semibold h-9 rounded-md"
            >
              Print Invoice
            </Button>
            <Button
              size="middle"
              onClick={downloadInvoice}
              id="download_invoice_pdf"
              className="flex-grow min-w-[80px] text-xs font-semibold h-9 rounded-md"
            >
              Download
            </Button>
            <Button
              size="middle"
              icon={<PrinterOutlined />}
              onClick={() => setCourierOpen(true)}
              id="print_courier_parcel"
              className="flex-grow min-w-[100px] text-xs font-semibold h-9 rounded-md"
            >
              Courier Print
            </Button>
          </div>
          <div className="flex flex-wrap lg:flex-nowrap gap-2 items-center border-t border-gray-150 pt-2 w-full">
            <Input
              placeholder="WhatsApp number (e.g. 0712345678)"
              prefix={<SendOutlined className="text-gray-400" />}
              value={whatsappPhone}
              onChange={(e) => setWhatsappPhone(e.target.value)}
              onPressEnter={handleSendWhatsApp}
              className="h-9 text-xs flex-grow w-full lg:w-auto"
              allowClear
            />
            <Button
              type="primary"
              size="middle"
              loading={sendInvoiceMutation.isPending}
              onClick={handleSendWhatsApp}
              id="send_invoice_whatsapp"
              className="text-xs font-semibold h-9 rounded-md bg-green-600 flex-grow lg:flex-none"
              disabled={!isWhatsAppEnabled || !isWhatsAppConnected}
            >
              Send Invoice
            </Button>
            <Button
              type="default"
              size="middle"
              loading={sendWhatsAppTextMutation.isPending}
              onClick={handleSendWhatsAppLink}
              id="send_link_whatsapp"
              icon={<WhatsAppOutlined className="text-green-500" />}
              className="text-xs font-semibold h-9 rounded-md flex items-center justify-center flex-grow lg:flex-none"
              disabled={!isWhatsAppEnabled || !isWhatsAppConnected}
            >
              Send Link
            </Button>
            <Button
              type="default"
              size="middle"
              loading={sendSmsMutation.isPending}
              onClick={handleSendSMSLink}
              id="send_link_sms"
              icon={<MessageOutlined className="text-blue-500" />}
              className="text-xs font-semibold h-9 rounded-md flex items-center justify-center flex-grow lg:flex-none"
              disabled={!isSmsGatewayEnable}
            >
              Send Link
            </Button>
          </div>
          <div className="hidden lg:flex justify-between items-center text-[10px] text-gray-400 px-1 border-t border-gray-150 pt-1.5 mt-0.5">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-150 border border-gray-200 rounded text-gray-500 font-mono text-[9px] leading-none">
                {SHORTCUTS.INVOICE_PRINT.PRINT_INVOICE.name}
              </kbd>
              Print
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-150 border border-gray-200 rounded text-gray-500 font-mono text-[9px] leading-none">
                {SHORTCUTS.INVOICE_PRINT.DOWNLOAD_PDF.name}
              </kbd>
              Download PDF
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-150 border border-gray-200 rounded text-gray-500 font-mono text-[9px] leading-none">
                {SHORTCUTS.INVOICE_PRINT.CHANGE_TEMPLATE.name}
              </kbd>
              Cycle Template
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-150 border border-gray-200 rounded text-gray-500 font-mono text-[9px] leading-none">
                {SHORTCUTS.INVOICE_PRINT.CHANGE_PAGE_SIZE.name}
              </kbd>
              Cycle Size
            </span>
          </div>
        </div>
      }
    >
      <>
        <div className="flex gap-5 justify-center">
          <Select
            size="middle"
            className="w-32"
            value={template}
            onChange={(e) => setTemplate(e)}
            options={INVOICE_TEMPLATES}
          />
          <Select
            size="middle"
            className="w-32"
            value={pageSize}
            onChange={(v) => setPageSize(v)}
            options={INVOICE_PAGE_SIZES}
          />
        </div>
        <div
          dangerouslySetInnerHTML={{ __html: getInvoiceHtml(false) }}
          style={{ width: "100%" }}
        />
      </>
      <CourierParcelDrawer
        open={courierOpen}
        order={order}
        onClose={() => setCourierOpen(false)}
      />
    </Drawer>
  );
};
